import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Users, UserCheck, Clock, Video, Eye } from 'lucide-react';
import { motion } from 'motion/react';
import { TextMorph } from 'torph/react';
import { useTilt } from '@/hooks/useTilt';
import type { AdminStats } from '../types';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
} from '../utils/adminAnimations';

interface AdminQuickStatsProps {
  stats: AdminStats;
}

const STAT_CARDS = [
  {
    key: 'users',
    icon: Users,
    labelKey: 'admin.totalUsers',
    getValue: (s: AdminStats) => s.totalUsers ?? 0,
    format: 'number' as const,
    link: '/admin/users',
  },
  {
    key: 'active',
    icon: UserCheck,
    labelKey: 'admin.activeUsers',
    getValue: (s: AdminStats) => s.activeUsers ?? 0,
    format: 'number' as const,
  },
  {
    key: 'pending',
    icon: Clock,
    labelKey: 'admin.pendingUsers',
    getValue: (s: AdminStats) => s.pendingUsers ?? 0,
    format: 'number' as const,
    highlight: (s: AdminStats) => (s.pendingUsers ?? 0) > 0,
  },
  {
    key: 'videos',
    icon: Video,
    labelKey: 'admin.totalVideos',
    getValue: (s: AdminStats) => s.totalVideos ?? 0,
    format: 'number' as const,
  },
  {
    key: 'views',
    icon: Eye,
    labelKey: 'admin.totalViews',
    getValue: (s: AdminStats) => s.totalViews ?? 0,
    format: 'number' as const,
  },
];

function AnimatedStatValue({ value, format }: { value: number; format: 'number' | 'currency' }) {
  const text = value.toLocaleString();
  return (
    <TextMorph as="span" duration={300} locale="he" respectReducedMotion>
      {text}
    </TextMorph>
  );
}

function StatCardWithTilt({
  card,
  stats,
  t,
  navigate,
}: {
  card: (typeof STAT_CARDS)[number];
  stats: AdminStats;
  t: (key: string) => string;
  navigate: (path: string) => void;
}) {
  const tiltRef = useRef<HTMLDivElement>(null);
  const { onMouseMove, onMouseLeave } = useTilt(tiltRef, { maxTilt: 4 });
  const Icon = card.icon;
  const value = card.getValue(stats);
  const isClickable = !!card.link;

  return (
    <motion.div
      variants={staggerItem}
      onClick={isClickable ? () => navigate(card.link!) : undefined}
    >
      <div
        ref={tiltRef}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        className={`rounded-3xl p-3 md:p-4 border border-white/30 transition-shadow glass-surface${isClickable ? ' cursor-pointer hover:shadow-sm' : ''}`}
      >
        <div className="flex items-center gap-2 mb-2">
          <Icon
            className="w-4 h-4 shrink-0"
            style={{ color: ADMIN_COLORS.navy + '80' }}
          />
          <p className="text-xs font-bold text-slate-500 uppercase">
            {t(card.labelKey)}
          </p>
        </div>
        <p className="text-2xl font-bold text-slate-800">
          <AnimatedStatValue value={value} format={card.format} />
        </p>
      </div>
    </motion.div>
  );
}

export function AdminQuickStats({ stats }: AdminQuickStatsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <motion.div
      className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:grid-cols-5"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {STAT_CARDS.map((card) => (
        <StatCardWithTilt
          key={card.key}
          card={card}
          stats={stats}
          t={t}
          navigate={navigate}
        />
      ))}
    </motion.div>
  );
}
